import { useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { C } from '../../theme/palette';

/** Longueur minimale exigée par le serveur pour un nouveau mot de passe. */
export const LONGUEUR_MINIMALE = 10;

/**
 * Ce qui empêche d'envoyer le formulaire, ou une chaîne vide s'il est prêt.
 *
 * @param {{ancien: string, nouveau: string, confirmation: string}} champs saisie
 * @returns {string} le message à afficher, vide si tout est en ordre
 */
export function motifDeRefus({ ancien, nouveau, confirmation }) {
  if (!ancien) return 'Saisis ton mot de passe actuel.';
  if (nouveau.length < LONGUEUR_MINIMALE)
    return `Le nouveau mot de passe doit comporter au moins ${LONGUEUR_MINIMALE} caractères.`;
  if (nouveau === ancien) return 'Le nouveau mot de passe doit différer de l’actuel.';
  if (nouveau !== confirmation) return 'La confirmation ne correspond pas.';
  return '';
}

/**
 * Dit si le compte connecté a un mot de passe à changer.
 *
 * <p>Chacun en a un, choisi en acceptant son invitation. Seul l'ancien compte
 * technique du code PIN n'en a pas : il porte un mot de passe tiré au hasard,
 * que personne ne connaît, et doit être réinvité.
 *
 * @param {object | null} session session en cours
 * @returns {boolean} vrai si le formulaire a un sens
 */
export function aUnMotDePasse(session) {
  if (!session) return false;
  if (session.accesParPin) return false;
  // Un ouvrier sans adresse n'est entré que par son code.
  if (session.role === 'ouvrier' && !session.email) return false;
  return true;
}

/**
 * Changer son mot de passe une fois connecté.
 *
 * <p>La page Sécurité conseillait un mot de passe fort sans offrir de le
 * changer : il fallait passer par « mot de passe oublié » et son courriel.
 * L'ouvrier invité par e-mail y a désormais accès : le message d'invitation
 * l'y renvoie. L'ancien compte du code PIN n'a rien à changer.
 *
 * @param {object} [props] propriétés
 * @param {string} [props.titre] titre de la carte
 * @param {string} [props.intro] texte d'introduction
 * @param {() => void} [props.onFait] appelé une fois le mot de passe changé
 * @returns {JSX.Element | null} la carte, ou rien sans mot de passe à changer
 */
export function MotDePasseCard({ titre = 'Changer mon mot de passe', intro, onFait } = {}) {
  const { session } = useSession();
  const [champs, setChamps] = useState({ ancien: '', nouveau: '', confirmation: '' });
  const [erreur, setErreur] = useState('');
  const [fait, setFait] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  if (!aUnMotDePasse(session)) return null;

  const pose = (cle) => (e) => {
    const valeur = e.target.value;
    setChamps((c) => ({ ...c, [cle]: valeur }));
    setErreur('');
    setFait(false);
  };
  const envoie = async () => {
    const motif = motifDeRefus(champs);
    if (motif) {
      setErreur(motif);
      return;
    }
    setEnvoi(true);
    try {
      await authApi.changeMotDePasse(champs.ancien, champs.nouveau);
      setChamps({ ancien: '', nouveau: '', confirmation: '' });
      setFait(true);
      if (typeof onFait === 'function') onFait();
    } catch (e) {
      setErreur(e?.message || 'Le mot de passe n’a pas pu être changé.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar icon="🔑" title={titre} color={C.navy} />
      <div style={{ padding: '13px 15px' }}>
        {intro && (
          <div style={{ fontSize: 11, color: C.ink, lineHeight: 1.55, marginBottom: 10 }}>
            {intro}
          </div>
        )}
        <Inp
          label="Mot de passe actuel"
          type="password"
          value={champs.ancien}
          onChange={pose('ancien')}
        />
        <Inp
          label="Nouveau mot de passe"
          type="password"
          placeholder={`${LONGUEUR_MINIMALE} caractères au moins`}
          value={champs.nouveau}
          onChange={pose('nouveau')}
        />
        <Inp
          label="Confirmer le nouveau"
          type="password"
          value={champs.confirmation}
          onChange={pose('confirmation')}
        />
        {erreur && (
          <div style={{ fontSize: 10, color: C.red, fontWeight: 700, marginBottom: 8 }}>
            {erreur}
          </div>
        )}
        {fait && (
          <div style={{ fontSize: 10, color: C.green, fontWeight: 700, marginBottom: 8 }}>
            Mot de passe changé. Il vaut dès ta prochaine connexion.
          </div>
        )}
        <Btn
          label={envoi ? 'Enregistrement…' : 'Enregistrer le nouveau mot de passe'}
          color={C.navy}
          full
          onClick={envoi ? undefined : envoie}
        />
      </div>
    </Card>
  );
}
